import React from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Image, Plus, Loader2, Download, Trash2 } from 'lucide-react';

/**
 * Gallery Card Component
 * Displays images and videos in a grid layout with thumbnails
 */
const GalleryCard = ({ 
  mediaFiles, 
  thumbnailUrls, 
  onAddMedia, 
  onFileClick, 
  onDownload, 
  onDelete, 
  uploading 
}) => {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Image className="w-4 h-4" /> Gallery
          </CardTitle>
          <Button 
            variant="outline" 
            size="sm"
            onClick={onAddMedia}
            disabled={uploading}
          >
            <Plus className="w-4 h-4 mr-1" /> Add Media
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {mediaFiles.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed rounded-lg">
            <Image className="w-10 h-10 mx-auto mb-2 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">No photos or videos yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {mediaFiles.map((file) => (
              <div 
                key={file.id} 
                className="group relative aspect-square rounded-lg overflow-hidden bg-muted/30 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={(e) => onFileClick(file, e)}
                data-testid={`gallery-tile-${file.id}`}
              >
                {thumbnailUrls[file.id] && thumbnailUrls[file.id] !== 'failed' ? (
                  <>
                    <img 
                      src={thumbnailUrls[file.id]}
                      alt={file.description || file.original_filename}
                      className="w-full h-full object-cover"
                    />
                    {/* Video play icon overlay */}
                    {file.mime_type?.startsWith('video/') && (
                      <>
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-12 h-12 bg-black/60 rounded-full flex items-center justify-center">
                            <div className="w-0 h-0 border-t-[8px] border-t-transparent border-l-[14px] border-l-white border-b-[8px] border-b-transparent ml-1" />
                          </div>
                        </div>
                        <div className="absolute bottom-2 left-2 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">
                          VIDEO
                        </div>
                      </>
                    )}
                  </>
                ) : file.mime_type?.startsWith('video/') && thumbnailUrls[file.id] === 'failed' ? (
                  /* Video fallback when thumbnail generation fails */
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-purple-900 to-purple-700">
                    <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mb-2">
                      <div className="w-0 h-0 border-t-[12px] border-t-transparent border-l-[20px] border-l-white border-b-[12px] border-b-transparent ml-1" />
                    </div>
                    <div className="absolute bottom-2 left-2 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">
                      VIDEO
                    </div>
                  </div>
                ) : file.mime_type?.startsWith('video/') ? (
                  /* Video loading state */
                  <div className="w-full h-full flex items-center justify-center bg-gray-800">
                    <Loader2 className="w-6 h-6 animate-spin text-white/50" />
                    <div className="absolute bottom-2 left-2 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">
                      VIDEO
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                )}
                
                {/* Overlay with name on hover */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2">
                  <p className="text-white text-xs truncate">{file.description || file.original_filename}</p>
                </div>
                
                {/* Action buttons */}
                <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <Button 
                    variant="secondary" 
                    size="icon"
                    className="h-6 w-6 bg-white/90 hover:bg-white shadow-sm"
                    onClick={(e) => { e.stopPropagation(); onDownload(file.id, file.original_filename, e); }}
                  >
                    <Download className="w-3 h-3" />
                  </Button>
                  <Button 
                    variant="secondary" 
                    size="icon"
                    className="h-6 w-6 bg-white/90 hover:bg-red-50 text-destructive shadow-sm"
                    onClick={(e) => { e.stopPropagation(); onDelete(file.id, e); }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                {/* Published indicator */}
                {file.published_to_portal && (
                  <div className="absolute top-1 left-1">
                    <div className="bg-primary text-white text-[9px] px-1.5 py-0.5 rounded font-medium">Published</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GalleryCard;
